import React, { useEffect, useState } from 'react';
import {
    TreeList,
    TreeListToolbar,
    mapTree,
    extendDataItem,
    TreeListTextEditor,
    moveTreeItem,
    TreeListDraggableRow
} from '@progress/kendo-react-treelist';

import { getMaxExpandedLevel, uuidv4 } from './TreeDropdownHelper';
import { getIdsForTreeData, findElementInTree, findElementByLevelIndex } from './TreeDropdownHelper';

const subItemsField = 'items';
const expandField = 'expanded';
const editField = 'inEdit';

const Actions = ({ dataItem, Add, Remove }) => {
    return (
        <td>
            <span className='action'>
                <button title='Add Child' className='ms-1 btn btn-light btn-sm p-0' onClick={() => Add(dataItem)}>
                    <i className='fas fa-plus p-1 text-success font-size-0'></i>
                </button>
                {dataItem.root !== true &&
                    <button title='Remove' className='ms-2 btn btn-light btn-sm p-0' onClick={() => Remove(dataItem)}>
                        <i className='fas fa-times p-1 text-danger font-size-0'></i>
                    </button>
                }
            </span>
        </td>
    );
};

function CustomCell(props) {
    const { dataItem, field, level, expanded, onExpandChange, moveUp, moveDown } = props;
    function handleExpand(event) {
        event.value = !expanded;
        event.dataItem = dataItem;
        onExpandChange(event, dataItem, level);
    }

    function handleEdit(event) {
        dataItem.onRowClick({ dataItem });
    }
    const [showDropdown, setShowDropdown] = useState(false);

    const handleMouseOver = () => {
        setShowDropdown(true);
    };

    const handleMouseOut = () => {
        setShowDropdown(false);
    };

    return (
        <td className='d-flex' onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
            {
                level.length > 1 && level.slice(0, level.length - 1).map((_, index) => <span className='k-icon k-i-none' key={index}></span>)
            }
            {
                dataItem.items && dataItem.items.length > 0 &&
                <>
                    {
                        expanded ?
                            <button className='btn p-0 hover-border-0 active-border-0 focus-border-0' onClick={handleExpand}>
                                <i class="fa-solid fa-caret-down font-size-3"></i>
                            </button>
                            :
                            <button className='btn p-0 hover-border-0 active-border-0 focus-border-0' onClick={handleExpand}>
                                <i class="fa-solid fa-caret-right font-size-3"></i>
                            </button>
                    }
                </>
            }
            <span className='ms-2 flex-1' onClick={handleEdit}>
                {dataItem[field].slice(0, 30) + (dataItem[field].length > 30 ? '...' : '')}
            </span>
            {dataItem.parentId &&
                <span className='ms-3' style={{ display: showDropdown ? 'inline' : 'none' }}>
                    {
                        !dataItem.isFirst &&
                        <button title='Move Up' className='ms-2 btn btn-link-secondary btn-sm p-0 px-2' disabled={dataItem.isFirst} onClick={() => moveUp(dataItem)}>
                            <i className='fas fa-arrow-up text-success'></i>
                        </button>
                    }
                    {
                        !dataItem.isLast &&
                        <button button title='Move Down' className='ms-2 btn btn-link-secondary btn-sm p-0 px-2' onClick={() => moveDown(dataItem)}>
                            <i className='fas fa-arrow-down text-primary'></i>
                        </button>
                    }
                </span>
            }
        </td >
    );
}

const TreeDropdown = ({ defaultTreeDropdown, onUpdate, isNew }) => {

    const [data, setData] = useState([]);
    const [expanded, setExpanded] = useState([]);
    const [expandedLevel, setExpandedLevel] = useState([0]);
    const [editId, setEditId] = useState(null);

    useEffect(() => {
        const data = JSON.parse(JSON.stringify(defaultTreeDropdown));
        addFirstLastBoolean(data);
        const defaultData = data.length > 0 ? data : [];
        const defaultEditId = isNew ? (data.length > 0 && !editId ? data[0].id : editId) : editId;
        setData(defaultData);
        setEditId(defaultEditId);
    }, [defaultTreeDropdown]);

    const addFirstLastBoolean = (array) => {
        array.forEach((item, index) => {
            if (index === 0) {
                item.isFirst = true;
            } else if (index !== 0 && item.isFirst) {
                delete item.isFirst;
            }
            if (index === array.length - 1) {
                item.isLast = true;
            } else if (index !== array.length - 1 && item.isLast) {
                delete item.isLast;
            }
            if (item.items && item.items.length > 0) {
                addFirstLastBoolean(item.items);
            }
        });
    };

    const getFormattedData = (data) => {
        const formattedData = JSON.parse(JSON.stringify(data));
        formattedData.forEach(item => {
            delete item.isFirst;
            delete item.isLast;
            if (item.items && item.items.length > 0) {
                getFormattedData(item.items);
            }
        });
        return formattedData;
    }

    const rowClick = event => {
        setEditId(prevEditId => (prevEditId === event.dataItem.id ? null : event.dataItem.id));
    };

    const onExpandChange = event => {
        const newExpandedLevel = !event.value ? [...expandedLevel, event.dataItem.expandedLevel] : expandedLevel.filter(level => level < event.dataItem.expandedLevel);
        setExpandedLevel(newExpandedLevel);
        const expandedIds = getIdsForTreeData(data, [], newExpandedLevel);
        setExpanded(expandedIds);
    };

    const onItemChange = event => {
        const newData = [...data];
        const element = findElementInTree(newData, event.dataItem);
        element.text = event.value;
        setData(newData);
        onUpdate(getFormattedData(newData));
    }

    const onItemAdded = parentItem => {
        const newData = [...data];
        const element = parentItem ? findElementInTree(newData, parentItem) : null;
        const newItem = {
            id: uuidv4(),
            text: "",
            parentId: element?.id || null,
            items: [],
            expandedLevel: element?.expandedLevel + 1 || 0
        };
        if (element?.items) {
            element.items = [...element.items, newItem];
        } else {
            newData.push(newItem);
        }
        addFirstLastBoolean(newData);
        setData(newData);
        setExpanded(prevExpanded => [...prevExpanded, parentItem?.id || newItem.id]);
        const addExpandedLevel = newItem.expandedLevel - 1;
        if (!expandedLevel.includes(addExpandedLevel)) {
            setExpandedLevel(prevExpandedLevel => [...prevExpandedLevel, newItem.expandedLevel - 1]);
        }
        setEditId(newItem.id);
        onUpdate(getFormattedData(newData));
    };

    const onItemRemoved = removeItem => {
        const newData = [...data];
        const element = findElementInTree(newData, removeItem);
        if (!element) {
            return;
        }
        const parentElement = findElementInTree(newData, { id: element.parentId });
        if (parentElement) {
            parentElement.items = parentElement.items.filter(item => item.id !== element.id);
        } else {
            newData.splice(newData.indexOf(element), 1);
        }
        const maxTreeLevel = getMaxExpandedLevel(newData);
        if (expandedLevel.includes(maxTreeLevel)) {
            setExpandedLevel(prevExpandedLevel => prevExpandedLevel.filter(level => level < maxTreeLevel));
        }
        addFirstLastBoolean(newData);
        setData(newData);
        setEditId(prevEditId => (prevEditId === removeItem.id ? null : prevEditId));
        onUpdate(getFormattedData(newData));
    };

    const expandAll = (e) => {
        e.preventDefault();
        const currentExpandedLevel = [...expandedLevel];
        const maxExpandedLevel = getMaxExpandedLevel(data);
        if (currentExpandedLevel.length === maxExpandedLevel) {
            return;
        }
        const newExpandedLevel = [...currentExpandedLevel, currentExpandedLevel.length];
        const expandedIds = getIdsForTreeData(data, [], newExpandedLevel);
        setExpandedLevel(newExpandedLevel);
        setExpanded(expandedIds);
    };

    const collapseAll = (e) => {
        e.preventDefault();
        const currentExpandedLevel = [...expandedLevel];
        if (currentExpandedLevel.length === 1) {
            return;
        }
        const newExpandedLevel = currentExpandedLevel.filter(level => level !== currentExpandedLevel.length - 1);
        const expandedIds = getIdsForTreeData(data, [], newExpandedLevel);
        setExpandedLevel(newExpandedLevel);
        setExpanded(expandedIds);
    };

    const onRowDrop = (event) => {
        const { dragged, draggedOver } = event;
        const draggedOverElement = findElementByLevelIndex(data, draggedOver)
        const draggedElement = findElementByLevelIndex(data, dragged);
        if (!draggedElement || !draggedOverElement) {
            return;
        }
        draggedElement.parentId = draggedOverElement.id;
        const newData = moveTreeItem(
            data,
            dragged,
            draggedOver,
            subItemsField
        );
        addFirstLastBoolean(newData);
        setData(newData);
        onUpdate(getFormattedData(newData))
    };

    const handleMoveUp = (dataItem) => {
        const element = findElementInTree(data, dataItem);
        if (!element) {
            return;
        }
        const parentElement = findElementInTree(data, { id: element.parentId });
        if (!parentElement) {
            return;
        }
        const index = parentElement.items.findIndex(item => item.id === element.id);
        if (index === 0) {
            return;
        }
        if (index - 1 === 0) {
            element.isFirst = true;
            delete parentElement.items[0].isFirst;
        }
        parentElement.items.splice(index, 1);
        parentElement.items.splice(index - 1, 0, element);
        addFirstLastBoolean(data);
        setData(data);
        onUpdate(getFormattedData(data));
    };

    const handleMoveDown = (dataItem) => {
        const element = findElementInTree(data, dataItem);
        if (!element) {
            return;
        }
        const parentElement = findElementInTree(data, { id: element.parentId });
        if (!parentElement) {
            return;
        }
        const index = parentElement.items.findIndex(item => item.id === element.id);
        if (index === parentElement.items.length - 1) {
            return;
        }
        if (index + 1 === parentElement.items.length) {
            element.isLast = true;
            delete parentElement.items[parentElement.items.length - 1].isLast;
        }
        parentElement.items.splice(index, 1);
        parentElement.items.splice(index + 1, 0, element);
        addFirstLastBoolean(data);
        setData(data);
        onUpdate(getFormattedData(data));
    }

    return (
        <TreeList
            style={{
                maxHeight: '500px',
                overflow: 'auto'
            }}
            className='tree-list-attribute-type'
            data={mapTree(data, subItemsField, (item, test, sdf) => {
                return extendDataItem(item, subItemsField, {
                    [expandField]: expanded.includes(item.id),
                    [editField]: item.id === editId,
                    onRowClick: rowClick,
                })
            })}
            row={TreeListDraggableRow}
            editField={editField}
            expandField={expandField}
            subItemsField={subItemsField}
            onRowClick={rowClick}
            onRowDrop={onRowDrop}
            onItemChange={onItemChange}
            onExpandChange={onExpandChange}
            columns={[
                {
                    cell: props => (
                        <Actions {...props} Add={onItemAdded} Remove={onItemRemoved} />
                    )
                },
                {
                    field: 'text',
                    width: "100%",
                    editCell: TreeListTextEditor,
                    expandable: true,
                    cell: props => (
                        <CustomCell {...props} moveUp={handleMoveUp} moveDown={handleMoveDown} />
                    )
                }
            ]}
            toolbar={
                <TreeListToolbar>
                    <button title='Expand' className='ms-2 btn btn-light btn-sm p-0' onClick={expandAll}>
                        <i className='fas fa-plus p-1 text-dark'></i>
                    </button>
                    <button title='Collapse' className='ms-1 btn btn-light btn-sm p-0' onClick={collapseAll}>
                        <i className='fas fa-minus p-1 text-dark'></i>
                    </button>
                </TreeListToolbar>
            }
        />
    );
};

export default TreeDropdown;